import React, { forwardRef, memo, useId } from 'react';
import classNames from 'classnames';
import Typography from '@lib/components/Typography/Typography';
import { CheckboxSizes } from '@lib/components/CheckBox/enums';
import toggleStyles from '@lib/components/CheckBoxAsToggle/CheckBoxAsToggle.module.scss';
import styles from './CheckBoxAsToggleCard.module.scss';

export interface CheckBoxAsToggleCardProps {
  /** Input name attribute */
  name?: string;
  /** Label text displayed next to toggle */
  label?: string;
  /** Description text displayed below label */
  description?: string;
  /** Whether the toggle is checked */
  checked?: boolean;
  /** Change handler */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Whether the toggle is disabled */
  disabled?: boolean;
  /** Toggle size */
  size?: CheckboxSizes;
  /** Additional class name for the wrapper */
  className?: string;
}

const CheckBoxAsToggleCard = forwardRef(
  (
    props: CheckBoxAsToggleCardProps,
    ref: React.ForwardedRef<HTMLInputElement>,
  ) => {
    const {
      name,
      label = '',
      description,
      checked,
      onChange,
      disabled,
      size = CheckboxSizes.Large,
      className,
    } = props;

    const inputId = `${name}-toggle-card-${useId()}`;

    const wrapperClassName = classNames(styles.cardWrapper, className, {
      [styles.cardWrapperDisabled]: disabled,
    });

    const toggleRootClassName = classNames(toggleStyles.toggleRoot, {
      [toggleStyles.toggleRootLarge]: size === CheckboxSizes.Large,
      [toggleStyles.toggleRootSmall]: size === CheckboxSizes.Small,
      [toggleStyles.toggleRootChecked]: checked,
    });

    return (
      <div className={wrapperClassName}>
        <input
          ref={ref}
          id={inputId}
          name={name}
          type="checkbox"
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className={toggleStyles.input}
        />
        <label htmlFor={inputId} className={styles.cardContent}>
          <div className={toggleRootClassName} />
          <div className={styles.textContent}>
            {!!label && (
              <Typography variant="body" strong>
                {label}
              </Typography>
            )}
            {!!description && (
              <Typography variant="caption" className={styles.description}>
                {description}
              </Typography>
            )}
          </div>
        </label>
      </div>
    );
  },
);

export default memo(CheckBoxAsToggleCard);
